import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function StudentLifeWellbeingForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    subtitle: '',
    description: '',
    pillars: [],
    supports: [],
    images: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadSection();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load section');
      
      const result = await response.json();
      if (!result.ok || !Array.isArray(result.data)) {
        throw new Error('Invalid section data');
      }

      const section = result.data.find(s => s.section_type === 'student-life-wellbeing');
      if (!section) throw new Error('Wellbeing section not found');

      setSectionId(section.id);

      // Parse content_published
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      
      const enData = published?.en || formData;
      setFormData({
        kicker: enData.kicker || '',
        title: enData.title || '',
        subtitle: enData.subtitle || '',
        description: enData.description || '',
        pillars: Array.isArray(enData.pillars) ? enData.pillars : [],
        supports: Array.isArray(enData.supports) ? enData.supports : [],
        images: Array.isArray(enData.images) ? enData.images : []
      });

      // Load pending changes
      let pendingFieldsMap = {};
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${section.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data?.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingFieldsMap[change.field_id] = true;
              }
            });
          }
        }
      }
      
      setPendingFields(pendingFieldsMap);
    } catch (err) {
      console.error('Load section error:', err);
      toast?.error?.('Failed to load wellbeing section');
    } finally {
      setLoading(false);
    }
  };

  const handleSupportChange = (index, field, value) => {
    const newSupports = [...formData.supports];
    newSupports[index] = { ...newSupports[index], [field]: value };
    setFormData({ ...formData, supports: newSupports });
  };

  const handleImageUpload = async (file, isSupport = false, supportIndex = null) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast?.error?.('Only JPG, PNG, WebP allowed');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast?.error?.('Image must be under 5MB');
      return;
    }

    const uploadKey = isSupport ? `support_${supportIndex}_image` : 'wellbeing_image';
    setImageUploading(prev => ({ ...prev, [uploadKey]: true }));
    setImageProgress(prev => ({ ...prev, [uploadKey]: 0 }));

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setImageProgress(prev => ({ ...prev, [uploadKey]: percentComplete }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const result = JSON.parse(xhr.responseText);
          if (result.ok && result.data && result.data.media_id) {
            if (isSupport && supportIndex !== null) {
              handleSupportChange(supportIndex, 'image', result.data.media_id);
            } else {
              const newImages = [...formData.images, result.data.media_id];
              setFormData({ ...formData, images: newImages });
            }
            toast?.success?.('Image uploaded');
          } else {
            toast?.error?.('Upload failed');
          }
        }
      });

      xhr.addEventListener('error', () => {
        toast?.error?.('Upload error');
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataObj);
    } catch (err) {
      console.error('Upload error:', err);
      toast?.error?.('Upload failed');
    } finally {
      setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const submitField = async (fieldId, value) => {
    if (!sectionId || submitting[fieldId]) return;
    
    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const response = await fetch(
        `${apiBase}/cms/section/${sectionId}/field`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            field_id: fieldId,
            value: value,
            locale: 'en'
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        toast?.success?.('Change submitted for approval');
      } else {
        toast?.error?.(result.message || 'Failed to submit');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast?.error?.('Submission failed');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (loading) {
    return <div className="form-loading">Loading wellbeing section...</div>;
  }

  return (
    <div className="admin-form">
      <ToastContainer />
      
      <h2>Wellbeing & Social Emotional Learning</h2>

      <div className="form-group">
        <label>Kicker</label>
        <input
          type="text"
          value={formData.kicker}
          onChange={(e) => setFormData({ ...formData, kicker: e.target.value })}
        />
        <button 
          onClick={() => submitField('kicker', formData.kicker)}
          disabled={submitting.kicker}
          className={pendingFields.kicker ? 'pending' : ''}
        >
          {submitting.kicker ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <button 
          onClick={() => submitField('title', formData.title)}
          disabled={submitting.title}
          className={pendingFields.title ? 'pending' : ''}
        >
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Subtitle</label>
        <input
          type="text"
          value={formData.subtitle}
          onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
        />
        <button 
          onClick={() => submitField('subtitle', formData.subtitle)}
          disabled={submitting.subtitle}
          className={pendingFields.subtitle ? 'pending' : ''}
        >
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
        />
        <button 
          onClick={() => submitField('description', formData.description)}
          disabled={submitting.description}
          className={pendingFields.description ? 'pending' : ''}
        >
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <h3>Core Pillars</h3>
      <div className="form-group">
        <label>Pillars (one per line)</label>
        <textarea
          value={formData.pillars.join('\n')}
          onChange={(e) => setFormData({ ...formData, pillars: e.target.value.split('\n').filter(p => p.trim()) })}
          rows={5}
        />
        <button 
          onClick={() => submitField('pillars', formData.pillars)}
          disabled={submitting.pillars}
          className={pendingFields.pillars ? 'pending' : ''}
        >
          {submitting.pillars ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <h3>Support Pathways</h3>
      {formData.supports.map((support, index) => (
        <div key={index} className="nested-item-card">
          <h4>Support {index + 1}: {support.title || 'Untitled'}</h4>
          
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={support.title || ''}
              onChange={(e) => handleSupportChange(index, 'title', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Detail Description</label>
            <textarea
              value={support.detail || ''}
              onChange={(e) => handleSupportChange(index, 'detail', e.target.value)}
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Icon (Font Awesome class)</label>
            <input
              type="text"
              value={support.icon || ''}
              onChange={(e) => handleSupportChange(index, 'icon', e.target.value)}
              placeholder="fa-user-tie"
            />
          </div>

          <button 
            onClick={() => submitField(`supports.${index}`, formData.supports[index])}
            disabled={submitting[`supports.${index}`]}
            className={pendingFields[`supports.${index}`] ? 'pending' : ''}
          >
            Submit Support
          </button>
        </div>
      ))}

      <h3>Section Images</h3>
      <div className="form-group">
        <label>Images (IDs)</label>
        <div className="media-list">
          {formData.images.map((imgId, idx) => (
            <div key={idx} className="media-item">
              <span>Media ID: {imgId}</span>
              {mediaMap[imgId] && (
                <img src={mediaMap[imgId]} alt="Wellbeing" style={{ maxWidth: '100px', maxHeight: '100px' }} />
              )}
            </div>
          ))}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleImageUpload(e.target.files?.[0])}
          disabled={imageUploading.wellbeing_image}
        />
        {imageUploading.wellbeing_image && (
          <div className="progress-bar">
            <div style={{ width: `${imageProgress.wellbeing_image}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}
